/**
 * Regular vs one-off income split for Financial Summary (deterministic).
 */

import { format, parseISO } from 'date-fns';
import type { FinancialSummaryTransaction } from './financial-summary-dataset';
import {
  buildReportingCategoryMetaMap,
  financialSummaryIncomeAmount,
  type ReportingCategoryRecord,
} from './reporting-transaction-flow';

export interface IncomeTransactionSlice {
  id: string;
  date: string;
  amount: number;
}

export interface MonthlyIncomeTotals {
  monthKey: string;
  monthLabel: string;
  income: number;
}

export interface OneOffIncomeAnalysis {
  totalIncome: number;
  regularIncome: number;
  oneOffIncome: number;
  medianMonthlyIncome: number;
  monthlyTotals: MonthlyIncomeTotals[];
  /** Month labels where income < 10% of median (when median > 0). */
  lowIncomeMonthLabels: string[];
  /** Single income transactions > 2× median monthly income. */
  largeSingleIncomeTransactions: IncomeTransactionSlice[];
  /** Single income transactions > 3× median (possible transfer). */
  possibleTransferIncomeTransactions: IncomeTransactionSlice[];
}

function median(values: number[]): number {
  const sorted = values.filter((v) => v > 0).sort((a, b) => a - b);
  if (!sorted.length) return 0;
  const mid = Math.floor(sorted.length / 2);
  return sorted.length % 2 === 0 ? (sorted[mid - 1] + sorted[mid]) / 2 : sorted[mid];
}

export function analyseFinancialSummaryIncome(
  transactions: FinancialSummaryTransaction[],
  categories: ReportingCategoryRecord[] = []
): OneOffIncomeAnalysis {
  const categoryMeta = buildReportingCategoryMetaMap(categories);
  const byMonth = new Map<string, number>();
  const incomeTxs: IncomeTransactionSlice[] = [];

  for (const tx of transactions) {
    const amount = financialSummaryIncomeAmount(tx, categoryMeta);
    if (amount <= 0) continue;
    incomeTxs.push({ id: tx.id, date: tx.date, amount });
    const monthKey = tx.date.slice(0, 7);
    byMonth.set(monthKey, (byMonth.get(monthKey) ?? 0) + amount);
  }

  const monthlyTotals: MonthlyIncomeTotals[] = Array.from(byMonth.entries())
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([monthKey, income]) => ({
      monthKey,
      monthLabel: format(parseISO(`${monthKey}-01`), 'MMM yyyy'),
      income,
    }));

  const medianMonthlyIncome = median(monthlyTotals.map((m) => m.income));
  const totalIncome = incomeTxs.reduce((s, t) => s + t.amount, 0);

  let oneOffFromSingles = 0;
  const largeSingleIncomeTransactions: IncomeTransactionSlice[] = [];
  const possibleTransferIncomeTransactions: IncomeTransactionSlice[] = [];

  if (medianMonthlyIncome > 0) {
    for (const tx of incomeTxs) {
      if (tx.amount > medianMonthlyIncome * 3) {
        possibleTransferIncomeTransactions.push(tx);
      }
      if (tx.amount > medianMonthlyIncome * 2) {
        largeSingleIncomeTransactions.push(tx);
        oneOffFromSingles += tx.amount;
      }
    }
  }

  let oneOffFromMonthlySpikes = 0;
  for (const month of monthlyTotals) {
    if (medianMonthlyIncome > 0 && month.income > medianMonthlyIncome * 2) {
      oneOffFromMonthlySpikes += month.income - medianMonthlyIncome;
    }
  }

  const oneOffIncome = Math.min(
    totalIncome,
    Math.max(oneOffFromSingles, oneOffFromMonthlySpikes)
  );
  const regularIncome = Math.max(0, totalIncome - oneOffIncome);

  const lowIncomeMonthLabels =
    medianMonthlyIncome > 0
      ? monthlyTotals
          .filter((m) => m.income < medianMonthlyIncome * 0.1)
          .map((m) => m.monthLabel)
      : [];

  return {
    totalIncome,
    regularIncome,
    oneOffIncome,
    medianMonthlyIncome,
    monthlyTotals,
    lowIncomeMonthLabels,
    largeSingleIncomeTransactions,
    possibleTransferIncomeTransactions,
  };
}

export function underlyingSavingsRate(
  regularIncome: number,
  totalExpenses: number
): number | null {
  if (regularIncome <= 0) return null;
  const net = regularIncome - totalExpenses;
  return Math.round((net / regularIncome) * 1000) / 10;
}
